import { apiCweb } from './api-cweb';
import {
  DbCommit,
  DbUser,
  DbProject,
  DbUserSettings,
  UserInformWithAccountViewModel,
} from '~src/types/db';
import { appLog } from '~src/utils/log';
import { DB_STATE } from '~src/types/enums';
import { sortBy } from 'lodash';

export interface LoadUserParams {
  projectId: string;
  user_id: number;
  user_name: string;
}
export const loadUser = async (
  key,
  { projectId, user_id, user_name }: LoadUserParams
) => {
  if (!user_id) return undefined;

  appLog('loadUser ->', { projectId });
  const res = await apiCweb.get<DbUser>(
    `/projects/${projectId}/users/${user_id}`,
    {
      validateStatus: (status) => (status ?? 0) < 500, // TV1-318 fix
    }
  );

  if (res.status === 404) return undefined;

  return res.data;
};

export const loadUserSettings = async (key: string) => {
  const res = await apiCweb.get<DbUserSettings>(`auth/user_settings`);

  return {
    ...res.data,
    // using seconds timestamp
    vacation: res.data.vacation ? res.data.vacation * 1000 : null,
  };
};

export interface SaveUserSettings {
  block_notification?: boolean;
  vacation?: number | null;
  frequency?: number | null;
  time_1?: string | null;
  time_2?: string | null;
  is_send_weekend?: boolean;
  is_tags_on?: boolean;
}

export const saveVacationSettings = async ({ vacation }: SaveUserSettings) => {
  const res = await apiCweb.put<SaveUserSettings>(`auth/user_settings`, {
    vacation,
  });

  return res.data;
};

export const saveUserSettings = async ({
  block_notification,
  frequency,
  time_1,
  time_2,
  is_send_weekend,
  is_tags_on,
}: SaveUserSettings) => {
  let action = 'update';
  switch (block_notification) {
    case true:
      action = 'block_all_on';
      break;
    case false:
      action = 'block_all_off';
      break;
    default:
      action = 'update';
  }
  if (action === 'update') {
    switch (is_send_weekend) {
      case true:
        action = 'send_weekend_on';
        break;
      case false:
        action = 'send_weekend_off';
        break;
      default:
        action = 'update';
    }
  }
  const res = await apiCweb.put<SaveUserSettings>(
    `current_user/global_notification_settings?action=${action}`,
    {
      frequency,
      time_1,
      time_2,
      is_tags_on,
    }
  );

  return res.data;
};

export const getCurrentUser = async () => {
  const res = await apiCweb.get<UserInformWithAccountViewModel>(
    `/current_user`
  );

  return res.data;
};

// EXISTS in API-CWEB.ts
// export const userLogin = async ({ data }) => {
//   const res = await apiCweb.post(`/auth/login`, data);
//   return res.data;
// };

export const validateUserToken = async () => {
  const res = await apiCweb.get(`/auth/check_token`);

  return res.data;
};

export const createUserCreationToken = async ({
  shareId,
  shareKey,
  email,
  language,
}: {
  shareId: string;
  shareKey: string;
  email: string;
  language?: string;
}) => {
  const res = await apiCweb.post(
    `/tender_shares/${shareId}/user_creation_token`,
    { email },
    {
      headers: {
        'Share-Key': shareKey,
      },
      params: {
        verify_code_email_language: language,
      },
    }
  );
  return res.data;
};

export const registerUserWithPrivateKey = async ({
  publicKey,
  privateKey,
  data,
}: {
  publicKey: string;
  privateKey: string;
  data: any;
}) => {
  const res = await apiCweb.post('/tender_shares/users', data, {
    headers: {
      'Public-Key': publicKey,
      'Private-Key': privateKey,
    },
  });
  return res.data;
};

// GET current user context for project application
export const getCurrentUserContext = async () => {
  const res = await apiCweb.get('/current_user/context');
  return res.data;
};

// POST project application
export const submitProjectApplication = async (data: any) => {
  const res = await apiCweb.post('/current_user/project_applications', data);
  return res.data;
};
